import type { WeightLogRow } from "@/lib/database.types";

// Aggregate to one point per calendar month (the latest entry of that month),
// then chart cumulative weight lost vs the earliest recorded weight.
function monthlySeries(logs: WeightLogRow[]): { label: string; weight: number }[] {
  if (logs.length === 0) return [];
  const byMonth = new Map<string, WeightLogRow>();
  for (const l of [...logs].sort((a, b) => a.recorded_at.localeCompare(b.recorded_at))) {
    const d = new Date(l.recorded_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    byMonth.set(key, l);
  }
  return Array.from(byMonth.entries())
    .slice(-6)
    .map(([key, log]) => {
      const [, m] = key.split("-");
      const monthName = new Date(2000, Number(m) - 1, 1).toLocaleString(undefined, { month: "short" });
      return { label: monthName, weight: Number(log.weight_lb) };
    });
}

export function WeightChart({ logs }: { logs: WeightLogRow[] }) {
  const series = monthlySeries(logs);
  if (series.length < 2) {
    return (
      <p className="text-[13px] text-fh-gray">
        We'll show your weight trend here once you log at least two months of data.
      </p>
    );
  }

  const start = series[0]!.weight;
  const lost = series.map((s) => Math.max(0, start - s.weight));
  const maxLost = Math.max(1, ...lost);

  return (
    <div>
      <div className="text-[13px] text-fh-gray mb-[18px]">Cumulative weight lost (lb)</div>
      <div className="flex items-end gap-4 h-[124px]">
        {lost.map((lb, i) => {
          const isLast = i === lost.length - 1;
          const heightPx = (lb / maxLost) * 88;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-2">
              <div className="text-[12px] font-semibold text-fh-green-dark">{lb.toFixed(0)}</div>
              <div
                className={`w-full max-w-12 rounded-t-md ${isLast ? "bg-fh-green" : "bg-fh-green-light"}`}
                style={{ height: `${heightPx}px` }}
              />
              <div className="text-[11.5px] text-fh-gray">{series[i]!.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
